import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  Line,
  ReferenceArea,
} from 'recharts';
import InfoTip from '../shared/InfoTip';
import { SkeletonChart } from '../shared/Skeleton';
import ThemedChartTooltip, { ThemedTooltipBox } from '../shared/ThemedChartTooltip';
import { fmtTrendDate as fmtDateLabel, dateToTs as toTs, findTrendGaps, timeAxisProps } from '../shared/trendAxis';

/* ----------------------------- component ----------------------------- */
const SessionsTrendChart = ({ data, previousData, comparisonMode, loading }) => {
  /* ---- loading state ---- */
  if (loading) {
    return (
      <div className="rotate-x-labels bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-5">
        <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center">Sessions Trend<InfoTip text="Daily session volume over the selected date range. When comparison mode is on, the previous period is overlaid as a dashed line so you can spot week-over-week or year-over-year changes." /></h2>
        <div className="flex flex-wrap items-center gap-4 mb-4 h-[20px]" />
        <SkeletonChart height={280} />
      </div>
    );
  }

  /* ---- empty state ---- */
  if (!data || data.length === 0) {
    return (
      <div className="rotate-x-labels bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-5">
        <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center">Sessions Trend<InfoTip text="Daily session volume over the selected date range. When comparison mode is on, the previous period is overlaid as a dashed line so you can spot week-over-week or year-over-year changes." /></h2>
        <div className="flex flex-wrap items-center gap-4 mb-4 h-[20px]" />
        <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400" style={{ height: 280 }}>
          No traffic data for this period
        </div>
      </div>
    );
  }

  /* ---- build chart data with comparison overlay ---- */
  const showComparison = comparisonMode !== 'off' && previousData && previousData.length > 0;

  // Previous period overlays by index (day-1-vs-day-1), so plot each previous datum
  // at the CURRENT period's timestamp for the same position — keeps the existing
  // positional comparison while the axis stays a true time scale.
  const chartData = data.map((day, index) => ({
    ts: toTs(day.date),
    dateLabel: fmtDateLabel(day.date),
    date: day.date,
    sessions: day.sessions,
    users: day.users,
    conversions: day.conversions,
    ...(showComparison
      ? {
          previousSessions: previousData[index]?.sessions ?? null,
          previousDate: previousData[index]?.date ?? null,
        }
      : {}),
  }));

  // Detect multi-day gaps in the data (days the API didn't return). The line is
  // drawn continuously across them (a solid bridge), but each gap range is marked
  // with a shaded band + an explanatory note on hover, so a missing stretch is
  // explained rather than silently interpolated.
  const gaps = findTrendGaps(chartData);

  /* ---- summary stats ---- */
  const totalSessions = data.reduce((sum, day) => sum + (day.sessions || 0), 0);
  const totalUsers = data.reduce((sum, day) => sum + (day.users || 0), 0);
  const totalConversions = data.reduce((sum, day) => sum + (day.conversions || 0), 0);
  const avgDailySessions = data.length > 0 ? Math.round(totalSessions / data.length) : 0;

  return (
    <div className="rotate-x-labels bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-5">
      <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center">Sessions Trend<InfoTip text="Daily session volume over the selected date range. When comparison mode is on, the previous period is overlaid as a dashed line so you can spot week-over-week or year-over-year changes." /></h2>

      {/* Legend + summary */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 bg-[#32B1FF] rounded-full inline-block"></span>
          <span className="text-xs text-gray-600 dark:text-gray-400">Sessions</span>
        </div>
        {showComparison && (
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-gray-400 rounded-full inline-block" style={{ borderBottom: '2px dashed #9ca3af', height: 0 }}></span>
            <span className="text-xs text-gray-600 dark:text-gray-400">Previous Period</span>
          </div>
        )}
        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
          <span>Total Sessions: <span className="font-semibold text-gray-700 dark:text-gray-200">{totalSessions.toLocaleString()}</span></span>
          <span>Total Users: <span className="font-semibold text-gray-700 dark:text-gray-200">{totalUsers.toLocaleString()}</span></span>
          <span>Conversions: <span className="font-semibold text-gray-700 dark:text-gray-200">{totalConversions.toLocaleString()}</span></span>
          <span>Avg Daily: <span className="font-semibold text-gray-700 dark:text-gray-200">{avgDailySessions.toLocaleString()}</span></span>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 18, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="sessionsAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#32B1FF" stopOpacity={0.15} />
              <stop offset="100%" stopColor="#32B1FF" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
          {/* Shade each missing-data range; the line bridges it, the band explains it.
              The SVG <title> shows a native "No data: …" tooltip on hover. */}
          {gaps.map((gap) => (
            <ReferenceArea
              key={gap.x1}
              x1={gap.x1}
              x2={gap.x2}
              fill="#9ca3af"
              fillOpacity={0.12}
              ifOverflow="extendDomain"
              label={{ value: 'no data', fontSize: 10, fill: '#9ca3af', position: 'insideTop' }}
            >
              <title>{gap.label}</title>
            </ReferenceArea>
          ))}
          {/* True time scale: x-values are epoch ms, so each day sits at its real
              calendar position. Missing days therefore show as an actual gap rather
              than being compressed into one equal-width category slot. */}
          <XAxis {...timeAxisProps()} />
          <YAxis
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            width={50}
          />
          <ThemedChartTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const datum = payload[0].payload;
              return (
                <ThemedTooltipBox title={datum.dateLabel}>
                  <div>Sessions: {datum.sessions?.toLocaleString()}</div>
                  <div>Users: {datum.users?.toLocaleString()}</div>
                  <div>Conversions: {datum.conversions?.toLocaleString()}</div>
                  {showComparison && datum.previousSessions != null && (
                    <>
                      <div style={{ borderTop: '1px solid rgba(148,163,184,0.25)', margin: '4px 0' }} />
                      <div style={{ color: '#9ca3af' }}>
                        Prev Sessions: {datum.previousSessions.toLocaleString()}
                        {datum.previousDate && <span style={{ marginLeft: 6, fontSize: 10 }}>({fmtDateLabel(datum.previousDate)})</span>}
                      </div>
                    </>
                  )}
                </ThemedTooltipBox>
              );
            }}
          />
          <Area type="monotone" dataKey="sessions" fill="url(#sessionsAreaGrad)" stroke="none" />
          <Line
            type="monotone"
            dataKey="sessions"
            stroke="#32B1FF"
            strokeWidth={2}
            dot={chartData.length <= 31}
            activeDot={{ r: 4 }}
          />
          {showComparison && (
            <Line
              type="monotone"
              dataKey="previousSessions"
              stroke="#9ca3af"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              dot={false}
              connectNulls
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SessionsTrendChart;
